import { useState } from "react";
import { GripVertical, Zap, Calendar, Trash2, Globe } from "lucide-react";
import { STAGES, TAG_STYLES, fmtMoney, fmtDate } from "@/lib/constants";

function ProspectCard({ p, onDragStart, onDelete, onOpen }) {
  return (
    <div
      data-testid={`prospect-card-${p.id}`}
      draggable
      onDragStart={(e) => onDragStart(e, p.id)}
      onClick={() => onOpen(p)}
      className="group bg-[#0D1322] border border-white/8 rounded-xl p-3.5 cursor-pointer active:cursor-grabbing hover:border-white/20 hover:-translate-y-0.5 transition-all"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-semibold text-white text-sm truncate">{p.name}</div>
          {p.company && (
            <div className="flex items-center gap-1 text-[11px] text-slate-500 truncate mt-0.5">
              <Globe className="w-3 h-3 shrink-0" />
              <span className="truncate">{p.company}</span>
            </div>
          )}
        </div>
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            data-testid={`delete-prospect-${p.id}`}
            onClick={(e) => {
              e.stopPropagation();
              onDelete(p.id);
            }}
            className="p-1 rounded text-slate-500 hover:text-red-400"
            title="Delete"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
          <GripVertical className="w-4 h-4 text-slate-600" />
        </div>
      </div>

      {(p.created_at || p.self_booked) && (
        <div className="flex items-center gap-1 mt-2 text-[11px] text-slate-400">
          {p.self_booked ? <Zap className="w-3 h-3 text-amber-400" /> : <Calendar className="w-3 h-3 text-slate-500" />}
          <span>{fmtDate(p.created_at)}</span>
        </div>
      )}

      {p.tags?.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {p.tags.map((t) => {
            const st = TAG_STYLES[t] || { bg: "rgba(148,163,184,0.15)", text: "#CBD5E1" };
            return (
              <span
                key={t}
                className="text-[10px] font-medium px-1.5 py-0.5 rounded"
                style={{ background: st.bg, color: st.text }}
              >
                {t}
              </span>
            );
          })}
        </div>
      )}

      <div className="flex items-center justify-between mt-3 pt-2.5 border-t border-white/5">
        <span className="text-[9px] font-mono-g uppercase tracking-widest text-slate-600">{p.source}</span>
        <span className="font-mono-g text-xs text-slate-300 font-semibold">{fmtMoney(p.monthly)}/mo</span>
      </div>
    </div>
  );
}

export default function ProspectsKanban({ prospects, onMove, onDelete, onOpen }) {
  const [dragOver, setDragOver] = useState(null);

  const onDragStart = (e, id) => {
    e.dataTransfer.setData("text/plain", id);
    e.dataTransfer.effectAllowed = "move";
  };
  const onDrop = (e, stageId) => {
    e.preventDefault();
    const id = e.dataTransfer.getData("text/plain");
    setDragOver(null);
    if (id) onMove(id, stageId);
  };

  return (
    <div className="flex gap-4 overflow-x-auto pb-4" data-testid="kanban-board">
      {STAGES.map((stage) => {
        const cards = prospects.filter((p) => p.stage === stage.id);
        const total = cards.reduce((s, p) => s + (p.monthly || 0), 0);
        const isOver = dragOver === stage.id;
        return (
          <div
            key={stage.id}
            data-testid={`kanban-column-${stage.id}`}
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(stage.id);
            }}
            onDragLeave={() => setDragOver((s) => (s === stage.id ? null : s))}
            onDrop={(e) => onDrop(e, stage.id)}
            className={`shrink-0 w-[290px] rounded-2xl p-3 transition-colors ${
              isOver ? "bg-white/5 ring-1 ring-indigo-500/40" : "bg-[#0B1120]/60"
            } border border-white/8`}
          >
            <div className="flex items-center justify-between mb-3 px-1">
              <div className="flex items-center gap-2 min-w-0">
                <span className="w-2 h-2 rounded-full shrink-0" style={{ background: stage.color }} />
                <span className="text-sm font-semibold text-white truncate">{stage.label}</span>
                <span className="text-xs text-slate-500 font-mono-g">{cards.length}</span>
              </div>
              <span className="text-[11px] font-mono-g text-slate-400 shrink-0">{fmtMoney(total)}/mo</span>
            </div>
            <div className="space-y-2.5 min-h-[80px]">
              {cards.map((p) => (
                <ProspectCard key={p.id} p={p} onDragStart={onDragStart} onDelete={onDelete} onOpen={onOpen} />
              ))}
              {cards.length === 0 && (
                <div className="text-center text-[11px] text-slate-600 py-8 border border-dashed border-white/8 rounded-xl">
                  Drop here
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
